/**
 * Browser SQLite database initialization and management
 * Uses sql.js for WebAssembly-based SQLite in the browser
 */

import initSqlJs from 'sql.js';
import type { Database } from 'sql.js';
import { getCurrencyFromLocale } from '~/lib/utils/currencyDetection';

const GUEST_DB_KEY = 'whencaniquit_guest_db';

/**
 * SQLite database singleton manager
 */
class SQLiteDatabaseManager {
  private db: Database | null = null;
  private initialized: boolean = false;

  /**
   * Initialize the SQLite database
   */
  async initialize(): Promise<Database> {
    if (this.db && this.initialized) {
      return this.db;
    }

    try {
      // Initialize sql.js with CDN-hosted wasm file
      const SQL = await initSqlJs({
        locateFile: (file: string) => `https://sql.js.org/dist/${file}`
      });

      // Try to load existing database from localStorage
      const savedDb = localStorage.getItem(GUEST_DB_KEY);
      if (savedDb) {
        const uint8Array = new Uint8Array(JSON.parse(savedDb));
        this.db = new SQL.Database(uint8Array);
        console.log('✅ Loaded existing guest database from localStorage');
      } else {
        this.db = new SQL.Database();
        await this.createSchema();
        console.log('✅ Created new guest database');
      }

      this.initialized = true;
      return this.db;
    } catch (error) {
      console.error('❌ Failed to initialize SQLite database:', error);
      throw new Error('Failed to initialize guest database');
    }
  }

  /**
   * Create database schema matching Directus structure
   */
  private async createSchema(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const schema = `
      -- Freedom Goals
      CREATE TABLE IF NOT EXISTS freedom_goals (
        id TEXT PRIMARY KEY,
        user_id TEXT NOT NULL,
        name TEXT NOT NULL,
        monthly_income_target REAL NOT NULL,
        required_capital REAL NOT NULL,
        retirement_target_age INTEGER DEFAULT 67,
        retirement_strategy TEXT DEFAULT 'safe_withdrawal',
        pension_start_age INTEGER,
        social_security_monthly REAL,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT
      );

      -- Assets
      CREATE TABLE IF NOT EXISTS assets (
        id TEXT PRIMARY KEY,
        user_id TEXT NOT NULL,
        type TEXT NOT NULL,
        name TEXT NOT NULL,
        description TEXT,
        is_active INTEGER DEFAULT 1,
        parent_asset_id TEXT,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT
      );

      -- Asset Transactions
      CREATE TABLE IF NOT EXISTS asset_transactions (
        id TEXT PRIMARY KEY,
        asset_id TEXT NOT NULL,
        transaction_type TEXT NOT NULL,
        quantity REAL NOT NULL,
        price_per_unit REAL NOT NULL,
        total_amount REAL NOT NULL,
        transaction_date TEXT NOT NULL,
        is_recurring INTEGER DEFAULT 0,
        recurring_frequency TEXT,
        recurring_start_date TEXT,
        recurring_end_date TEXT,
        recurring_day_of_month INTEGER,
        recurring_day_of_week INTEGER,
        path_name TEXT,
        metadata TEXT,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT,
        FOREIGN KEY (asset_id) REFERENCES assets(id) ON DELETE CASCADE
      );

      -- Stock/ETF Assets
      CREATE TABLE IF NOT EXISTS stock_etf_assets (
        id TEXT PRIMARY KEY,
        asset_id TEXT NOT NULL UNIQUE,
        symbol TEXT NOT NULL,
        exchange TEXT NOT NULL,
        sector TEXT,
        market_cap TEXT,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT,
        FOREIGN KEY (asset_id) REFERENCES assets(id) ON DELETE CASCADE
      );

      -- Crypto Assets
      CREATE TABLE IF NOT EXISTS crypto_assets (
        id TEXT PRIMARY KEY,
        asset_id TEXT NOT NULL UNIQUE,
        symbol TEXT NOT NULL,
        blockchain TEXT,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT,
        FOREIGN KEY (asset_id) REFERENCES assets(id) ON DELETE CASCADE
      );

      -- Savings Assets
      CREATE TABLE IF NOT EXISTS savings_assets (
        id TEXT PRIMARY KEY,
        asset_id TEXT NOT NULL UNIQUE,
        institution TEXT NOT NULL,
        account_type TEXT NOT NULL,
        current_apy REAL,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT,
        FOREIGN KEY (asset_id) REFERENCES assets(id) ON DELETE CASCADE
      );

      -- Real Estate Assets
      CREATE TABLE IF NOT EXISTS real_estate_assets (
        id TEXT PRIMARY KEY,
        asset_id TEXT NOT NULL UNIQUE,
        property_type TEXT NOT NULL,
        address TEXT NOT NULL,
        purchase_price REAL,
        current_market_value REAL NOT NULL,
        purchase_date TEXT NOT NULL,
        last_appraisal_date TEXT,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT,
        FOREIGN KEY (asset_id) REFERENCES assets(id) ON DELETE CASCADE
      );

      -- Mortgage Assets
      CREATE TABLE IF NOT EXISTS mortgage_assets (
        id TEXT PRIMARY KEY,
        asset_id TEXT NOT NULL UNIQUE,
        property_address TEXT NOT NULL,
        original_loan_amount REAL NOT NULL,
        lender TEXT,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT,
        FOREIGN KEY (asset_id) REFERENCES assets(id) ON DELETE CASCADE
      );

      -- Loan Assets
      CREATE TABLE IF NOT EXISTS loan_assets (
        id TEXT PRIMARY KEY,
        asset_id TEXT NOT NULL UNIQUE,
        loan_type TEXT NOT NULL,
        original_amount REAL NOT NULL,
        current_balance REAL NOT NULL,
        interest_rate REAL NOT NULL,
        monthly_payment REAL NOT NULL,
        loan_start_date TEXT NOT NULL,
        loan_term_months INTEGER,
        lender TEXT,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT,
        FOREIGN KEY (asset_id) REFERENCES assets(id) ON DELETE CASCADE
      );

      -- Rental Payment Assets
      CREATE TABLE IF NOT EXISTS rental_payment_assets (
        id TEXT PRIMARY KEY,
        asset_id TEXT NOT NULL UNIQUE,
        property_address TEXT NOT NULL,
        monthly_rent REAL NOT NULL,
        landlord_name TEXT,
        lease_start_date TEXT,
        lease_end_date TEXT,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT,
        FOREIGN KEY (asset_id) REFERENCES assets(id) ON DELETE CASCADE
      );

      -- Rental Income Assets
      CREATE TABLE IF NOT EXISTS rental_income_assets (
        id TEXT PRIMARY KEY,
        asset_id TEXT NOT NULL UNIQUE,
        property_address TEXT NOT NULL,
        monthly_rent REAL NOT NULL,
        tenant_name TEXT,
        lease_start_date TEXT,
        lease_end_date TEXT,
        connected_real_estate_asset_id TEXT,
        status TEXT DEFAULT 'published',
        sort INTEGER,
        user_created TEXT,
        date_created TEXT,
        user_updated TEXT,
        date_updated TEXT,
        deleted_at TEXT,
        FOREIGN KEY (asset_id) REFERENCES assets(id) ON DELETE CASCADE
      );

      -- User Settings (stores profile data locally)
      CREATE TABLE IF NOT EXISTS user_settings (
        id TEXT PRIMARY KEY,
        user_id TEXT NOT NULL UNIQUE,
        first_name TEXT,
        last_name TEXT,
        avatar TEXT,
        birth_date TEXT,
        base_currency TEXT DEFAULT 'USD',
        status TEXT DEFAULT 'published',
        date_created TEXT,
        date_updated TEXT
      );

      -- Create indexes for common queries
      CREATE INDEX IF NOT EXISTS idx_assets_user_id ON assets(user_id);
      CREATE INDEX IF NOT EXISTS idx_assets_type ON assets(type);
      CREATE INDEX IF NOT EXISTS idx_transactions_asset_id ON asset_transactions(asset_id);
      CREATE INDEX IF NOT EXISTS idx_transactions_date ON asset_transactions(transaction_date);
      CREATE INDEX IF NOT EXISTS idx_freedom_goals_user_id ON freedom_goals(user_id);
      CREATE INDEX IF NOT EXISTS idx_user_settings_user_id ON user_settings(user_id);
    `;

    this.db.exec(schema);
    
    // Initialize default user settings with location-based currency
    const detectedCurrency = getCurrencyFromLocale();
    this.db.exec(`
      INSERT OR IGNORE INTO user_settings (id, user_id, first_name, last_name, base_currency, date_created, date_updated)
      VALUES ('guest-settings-id', 'guest-user-00000000-0000-0000-0000-000000000000', '', '', '${detectedCurrency}', datetime('now'), datetime('now'))
    `);
  }

  /**
   * Get the current database instance
   */
  getDatabase(): Database {
    if (!this.db || !this.initialized) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    return this.db;
  }

  /**
   * Save database to localStorage
   */
  save(): void {
    if (!this.db) return;

    try {
      const data = this.db.export();
      const dataStr = JSON.stringify(Array.from(data));
      localStorage.setItem(GUEST_DB_KEY, dataStr);
      console.log('✅ Saved guest database to localStorage');
    } catch (error) {
      console.error('❌ Failed to save database:', error);
    }
  }

  /**
   * Clear the guest database
   */
  clear(): void {
    try {
      localStorage.removeItem(GUEST_DB_KEY);
      this.db?.close();
      this.db = null;
      this.initialized = false;
      console.log('✅ Cleared guest database');
    } catch (error) {
      console.error('❌ Failed to clear database:', error);
    }
  }
  
  /**
   * Clear database (alias for clear method)
   */
  clearDatabase(): void {
    return this.clear();
  }

  /**
   * Export database as JSON (for debugging or migration)
   */
  exportAsJSON(): string {
    if (!this.db) throw new Error('Database not initialized');

    const tables = [
      'freedom_goals',
      'assets',
      'asset_transactions',
      'stock_etf_assets',
      'crypto_assets',
      'savings_assets',
      'real_estate_assets',
      'mortgage_assets',
      'loan_assets',
      'rental_payment_assets',
      'rental_income_assets'
    ];

    const data: Record<string, any[]> = {};

    tables.forEach(table => {
      const result = this.db!.exec(`SELECT * FROM ${table}`);
      if (result.length > 0) {
        const columns = result[0].columns;
        data[table] = result[0].values.map((row: any[]) => {
          const obj: Record<string, any> = {};
          columns.forEach((col: string, idx: number) => {
            obj[col] = row[idx];
          });
          return obj;
        });
      } else {
        data[table] = [];
      }
    });

    return JSON.stringify(data, null, 2);
  }
}

// Export singleton instance
export const sqliteDb = new SQLiteDatabaseManager();

